import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuditLog } from "@ryde/redux";
import {
  errors,
  IFetchProcessesDto,
  IHistoryLogDrawerProps,
  IInjectedDrawerProps,
  PLURAL_KEY,
  ProcessesApi,
  warnAndToastException,
} from "@ryde/shared";

import { BaseDrawer, DrawerHeaderWithSubtitle } from "../drawer";
import {
  AuditLogTabContent,
  ProcessesTabContent,
  RideHistoryLogTabs,
} from "./components";
import { RideHistoryLogDrawerContentStyled } from "./ride-history-log-drawer.styled";

const RIDE_HISTORY_LOG_SEARCH_WIDTH_PX = "1300px";

export const RideHistoryLogDrawer: FC<
  IHistoryLogDrawerProps & IInjectedDrawerProps
> = ({ onCloseDrawer, drawerNumber, id, entity }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"audit" | "processes">("audit");
  const [processesCount, setProcessesCount] = useState(0);
  const { auditLogs } = useAuditLog(entity);

  const currentEntityName = t(`${entity}.${PLURAL_KEY}`);

  useEffect(() => {
    const fetchProcessesCount = async (): Promise<void> => {
      try {
        const dto: IFetchProcessesDto = {
          entityName: entity,
          affectedId: id,
        };
        const data = await ProcessesApi.fetchProcesses(dto);
        setProcessesCount(data.length);
      } catch (err) {
        warnAndToastException({
          error: err,
          fallbackTranslationKey: errors.processes.errorFetchingProcessesCount,
          translationFunction: t,
          logWarnMessage: "Failed to fetch processes count",
        });
        setProcessesCount(0);
      }
    };

    fetchProcessesCount();
  }, [entity, id, t]);

  return (
    <BaseDrawer
      customWidth={RIDE_HISTORY_LOG_SEARCH_WIDTH_PX}
      onCloseDrawer={onCloseDrawer}
      drawerNumber={drawerNumber}
    >
      <DrawerHeaderWithSubtitle
        onCloseDrawer={onCloseDrawer}
        title={t("general.HISTORY")}
        subTitle={` (${currentEntityName}) `}
      />
      <RideHistoryLogDrawerContentStyled>
        <RideHistoryLogTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          auditLogsCount={auditLogs.length}
          processesCount={processesCount}
        />
        {activeTab === "audit" && (
          <AuditLogTabContent id={id} entity={entity} />
        )}
        {activeTab === "processes" && (
          <ProcessesTabContent id={id} entity={entity} />
        )}
      </RideHistoryLogDrawerContentStyled>
    </BaseDrawer>
  );
};
